import React, { useContext, useEffect, useRef, useState } from 'react';
import type { NotificationTO } from '../lib';
import { useTriggerUpdate } from './TriggerUpdateContext';
import { useSelector } from 'react-redux';
import type { RootState } from '../store';
import { post } from '../api/baseUrl';

interface NotificationContextType {
  notifications: NotificationTO[];
  setNotifications: (notifications: NotificationTO[]) => void;
  disabled: boolean;
  setDisabled: (disabled: boolean) => void;
}
const NotificationContext = React.createContext<NotificationContextType>({
  notifications: [],
  setNotifications: () => {},
  disabled: false,
  setDisabled: () => {},
});

export const useNotifications = () => useContext(NotificationContext);

const NotificationProvider = ({ children }: { children: React.ReactNode }) => {
  const [disabled, setDisabled] = React.useState(false);
  const { updateNotificationCount } = useTriggerUpdate();
  const [updateCount, setUpdateCount] = useState(updateNotificationCount);
  const { selectedTenancy } = useSelector(
    (state: RootState) => state.clinicianProfile,
  );
  const [notifications, setNotifications] = React.useState<NotificationTO[]>(
    [],
  );

  const isInitialLoad = useRef(true);

  useEffect(() => {
    if (!selectedTenancy?.tenancy_id) {
      return;
    }

    const fetchNotifications = async () => {
      try {
        setDisabled(true);
        const {
          data: { data },
        } = await post('/getNotifications', {
          tenancyId: selectedTenancy.tenancy_id,
        });
        setNotifications(data);
      } catch (error) {
        console.error(error);
      } finally {
        setDisabled(false);
      }
    };

    if (isInitialLoad.current) {
      fetchNotifications();
      isInitialLoad.current = false;
      return;
    }
    if (updateCount !== updateNotificationCount) {
      fetchNotifications();
      setUpdateCount(updateNotificationCount);
    }
  }, [selectedTenancy?.tenancy_id, updateNotificationCount]);

  return (
    <NotificationContext.Provider
      value={{ disabled, setDisabled, notifications, setNotifications }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationProvider;
